// Copyright (c) Docugami, Inc. All rights reserved.

import * as globalImportUrl from 'url';
import { Configuration } from './configuration';
import globalAxios, { AxiosPromise, AxiosInstance } from 'axios';
import { BASE_PATH, RequestArgs, BaseAPI, RequiredError } from './base';
import { Config } from 'config';
import { WorkPathType } from './WorkPathApi';
import { BuildType } from './BuildType';
import { BuildState } from './api';

// Must be kept in-sync with AppNotification.cs
export enum NotificationCategory {
    Other = 'Other',
    Create = 'Create',
    Understand = 'Understand',
    Documents = 'Documents',
    WorkFlow = 'WorkFlow'
}

// Must be kept in-sync with AppNotification.cs
export enum NotificationType {
    BuildStatus = 'BuildStatus',
    WorkPathStatus = 'WorkPathStatus',
    WorkPathCta = 'WorkPathCta',
    FileUpload = "FileUpload"
}

// Must be kept in-sync with AppNotification.cs
export enum InteractionStates {
    None = 0,
    Read = 1 << 0,
    Deleted = 1 << 1
}

// Must be kept in-sync with NotificationSource.cs
export enum SourceType {
    System = 'System',
    Local = 'Local',
    Build = 'Build',
    WorkPath = 'WorkPath'
}

// Must be kept in-sync with WorkPathStatusContext.cs
export enum WorkPathStatusContextType {
    Draft = 'Draft',
    Unblocked = 'Unblocked'
}

// Must be kept in-sync with NotificationSource.cs
export interface NotificationSource {
    source: SourceType;
    id?: string;
}

// Must be kept in-sync with BindingContext.cs
export interface NotificationContext {
    model: WorkPathCtaContext | WorkPathStatusContext | BuildStatusContext;
}

// Must be kept in-sync with WorkPathStatusContext.cs
export interface WorkPathStatusContext {
    type: WorkPathStatusContextType;
    workPathType: WorkPathType;
    workPathName: string;
}

// Must be kept in-sync with WorkPathCtaContext.cs
export interface WorkPathCtaContext {
    useSampleDocSets: boolean;
    workPathType: WorkPathType;
}

// Must be kept in-sync with BuildStatusContext.cs
export interface BuildStatusContext {
    type: BuildType;
    state: BuildState;
    docSetId: string;
}

// Must be kept in-sync with AppNotification.cs
export interface NotificationModel {
    id: string;
    userId: string;
    source: NotificationSource;
    notificationType: NotificationType;
    createdAt: Date;
    category: NotificationCategory;
    interactions: InteractionStates;
    context: NotificationContext;
}

export const NotificationsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        getNotifications(orgId: string, options: any = {}): RequestArgs {
            // verify required parameter 'orgId' is not null or undefined
            if (orgId === null || orgId === undefined) {
                throw new RequiredError('orgId', 'Required parameter orgId was null or undefined when calling getNotifications.');
            }

            const localVarPath = `/notifications/${encodeURIComponent(String(orgId))}`;
            const localVarUrlObj = globalImportUrl.parse(localVarPath, true);

            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options };
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarUrlObj.query = { ...localVarUrlObj.query, ...localVarQueryParameter, ...options.query };
            // fix override query string Detail: https://stackoverflow.com/a/7517673/1077943
            localVarUrlObj.search = null;
            localVarRequestOptions.headers = { ...localVarHeaderParameter, ...options.headers };

            return {
                url: globalImportUrl.format(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },

        markAsRead(orgId: string, ids: string[], options: any = {}): RequestArgs {
            // verify required parameter 'orgId' is not null or undefined
            if (orgId === null || orgId === undefined) {
                throw new RequiredError('orgId', 'Required parameter orgId was null or undefined when calling markAsRead.');
            }

            // verify required parameter 'ids' is not empty
            if (ids.length === 0) {
                throw new RequiredError('ids', 'Required parameter ids was empty when calling markAsRead.');
            }

            const localVarPath = `/notifications/{orgId}/read`
                .replace(`{${"orgId"}}`, encodeURIComponent(String(orgId)));
            const localVarUrlObj = globalImportUrl.parse(localVarPath, true);

            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options };
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json-patch+json';

            localVarUrlObj.query = { ...localVarUrlObj.query, ...localVarQueryParameter, ...options.query };
            // fix override query string Detail: https://stackoverflow.com/a/7517673/1077943
            localVarUrlObj.search = null;
            localVarRequestOptions.headers = { ...localVarHeaderParameter, ...options.headers };
            localVarRequestOptions.data = JSON.stringify(ids);

            return {
                url: globalImportUrl.format(localVarUrlObj),
                options: localVarRequestOptions,
            };
        }
    }
}

export const NotificationsApiFp = function (configuration?: Configuration) {
    return {
        getNotifications(orgId: string, options?: any): (axios?: AxiosInstance, basePath?: string) => AxiosPromise<NotificationModel[]> {
            const localVarAxiosArgs = NotificationsApiAxiosParamCreator(configuration).getNotifications(orgId, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs = { ...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url };
                return axios.request(axiosRequestArgs);
            };
        },

        markAsRead(orgId: string, ids: string[], options?: any): (axios?: AxiosInstance, basePath?: string) => AxiosPromise {
            const localVarAxiosArgs = NotificationsApiAxiosParamCreator(configuration).markAsRead(orgId, ids, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs = { ...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url };
                return axios.request(axiosRequestArgs);
            };
        }
    }
}

export const NotificationsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    return {
        getNotifications(orgId: string, options?: any) {
            return NotificationsApiFp(configuration).getNotifications(orgId, options)(axios, basePath);
        },

        markAsRead(orgId: string, ids: string[], options?: any) {
            return NotificationsApiFp(configuration).markAsRead(orgId, ids, options)(axios, basePath);
        }
    }
}

export class NotificationsApi extends BaseAPI {
    constructor() {
        const config: Configuration = {
            basePath: Config.notificationsApiUrl.replace(/\/+$/, "")
        }

        super(config);
    }

    public getNotifications(orgId: string, options?: any) {
        return NotificationsApiFp(this.configuration).getNotifications(orgId, options)(this.axios, this.basePath);
    }

    public markAsRead(orgId: string, ids: string[], options?: any) {
        return NotificationsApiFp(this.configuration).markAsRead(orgId, ids, options)(this.axios, this.basePath);
    }
}
